<template>
  <div>
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">Crear Nuevo Albarán</h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
        <div class="col-lg-6 col-5 text-right">
          <base-button
            size="sm"
            type="neutral"
            @click="goBack"
          >
            <i class="fas fa-arrow-left mr-2"></i>
            Volver
          </base-button>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <div class="row">
        <div class="col-12">
          <card>
            <template v-slot:header>
              <h3 class="mb-0">Información del Albarán</h3>
            </template>
            
            <div class="card-body">
              <form @submit.prevent="createAlbaran">
                <div class="row">
                  <!-- Título -->
                  <div class="col-md-8">
                    <base-input
                      label="Título del Albarán"
                      v-model="albaranData.titulo"
                      type="text"
                      placeholder="Título descriptivo del albarán"
                      required
                    />
                  </div>
                  <!-- Fecha de entrega -->
                  <div class="col-md-4">
                    <base-input
                      label="Fecha de Entrega"
                      v-model="albaranData.fecha_entrega"
                      type="date"
                      required
                    />
                  </div>
                </div>

                <!-- Descripción -->
                <div class="row">
                  <div class="col-12">
                    <div class="form-group">
                      <label class="form-control-label">Descripción (opcional)</label>
                      <textarea
                        v-model="albaranData.descripcion"
                        class="form-control"
                        rows="3"
                        placeholder="Descripción detallada del albarán"
                      ></textarea>
                    </div>
                  </div>
                </div>

                <!-- Observaciones -->
                <div class="row">
                  <div class="col-12">
                    <div class="form-group">
                      <label class="form-control-label">Observaciones (opcional)</label>
                      <textarea
                        v-model="albaranData.observaciones"
                        class="form-control"
                        rows="3"
                        placeholder="Observaciones adicionales"
                      ></textarea>
                    </div>
                  </div>
                </div>

                <!-- Botones de acción -->
                <div class="row mt-4">
                  <div class="col-12 text-right">
                    <base-button
                      type="secondary"
                      @click="goBack"
                      class="mr-3"
                    >
                      Cancelar
                    </base-button>
                    <base-button
                      type="primary"
                      @click="createAlbaran"
                      :loading="creating"
                      :disabled="!isValidForm"
                    >
                      <i class="fas fa-save mr-2"></i>
                      Crear Albarán
                    </base-button>
                  </div>
                </div>
              </form>
            </div>
          </card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import "moment/locale/es";
import axios from 'axios';

moment.locale("es");

export default {
  name: "add-albaran",
  data() {
    return {
      creating: false,
      albaranData: {
        titulo: '',
        descripcion: '',
        observaciones: '',
        fecha_entrega: moment().format('YYYY-MM-DD') // Fecha de hoy por defecto
      }
    };
  },
  computed: {
    isValidForm() {
      return this.albaranData.titulo && 
             this.albaranData.titulo.trim().length > 0 &&
             this.albaranData.fecha_entrega;
    }
  },
  methods: {
    goBack() {
      this.$router.push({ name: "List Albaran" });
    },

    async generateNextAlbaranNumber() {
      try {
        // Obtener albaranes ordenados por número descendente para encontrar el más alto con formato ST-
        const apiUrl = process.env.VUE_APP_API_URL || 'https://misepis-api.delfromsoftware.com/api';
        
        // Buscar los últimos albaranes ordenados por número (más recientes primero)
        // Aumentamos el pageSize para asegurarnos de encontrar albaranes con formato ST-
        const response = await axios.get(
          `${apiUrl}/albaran-activos?sort=numero:desc&pagination[pageSize]=500`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        let nextNumber = 1;
        let maxNumber = 0;
        
        if (response.data.data && response.data.data.length > 0) {
          // Buscar el número más alto entre todos los albaranes con formato ST-
          response.data.data.forEach(albaran => {
            const numero = albaran.attributes?.numero || albaran.numero;
            if (numero && typeof numero === 'string' && numero.startsWith('ST-')) {
          // Extraer el número del formato ST-XXXX
              const match = numero.match(/^ST-(\d+)$/);
          if (match) {
                const num = parseInt(match[1], 10);
                if (!isNaN(num) && num > maxNumber) {
                  maxNumber = num;
                }
              }
            }
          });
          
          if (maxNumber > 0) {
            nextNumber = maxNumber + 1;
            console.log(`📝 [NUMERO] Último número ST- encontrado: ST-${maxNumber.toString().padStart(4, '0')}, siguiente: ST-${nextNumber.toString().padStart(4, '0')}`);
          } else {
            console.log('📝 [NUMERO] No se encontraron albaranes con formato ST-, usando número inicial: ST-0001');
          }
        } else {
          console.log('📝 [NUMERO] No hay albaranes en el sistema, usando número inicial: ST-0001');
        }

        // Formatear como ST-XXXX (4 dígitos con ceros a la izquierda)
        const nextNumberFormatted = `ST-${nextNumber.toString().padStart(4, '0')}`;
        console.log(`📝 [NUMERO] Número generado: ${nextNumberFormatted}`);
        return nextNumberFormatted;
      } catch (error) {
        console.error("❌ [NUMERO] Error generando número de albarán:", error);
        console.error("❌ [NUMERO] Error details:", error.response?.data || error.message);
        // Fallback: usar número secuencial basado en timestamp si hay error
        const fallbackNumber = moment().format('YYMMDDHHmmss');
        console.log(`📝 [NUMERO] Usando número de fallback: ST-${fallbackNumber}`);
        return `ST-${fallbackNumber}`;
      }
    },

    async createAlbaran() {
      if (!this.isValidForm) {
        this.$notify({
          type: "warning",
          message: "Por favor, ingresa un título para el albarán"
        });
        return;
      }

      try {
        this.creating = true;

        const apiUrl = process.env.VUE_APP_API_URL || 'https://misepis-api.delfromsoftware.com/api';
        
        // Generar número de albarán automáticamente
        const numero = await this.generateNextAlbaranNumber();
        
        // Preparar los datos del albarán
        const albaranPayload = {
          data: {
            numero: numero,
            titulo: this.albaranData.titulo.trim(),
            descripcion: this.albaranData.descripcion.trim() || null,
            observaciones: this.albaranData.observaciones.trim() || null,
            fecha_entrega: this.albaranData.fecha_entrega, // Fecha de entrega
            estado: 'pendiente_asignar', // Estado por defecto
            es_clon: false, // No es clon por defecto
            // Generar QR único automáticamente
            qr: `ALB${moment().format('YYMMDDHHmmss')}${Math.random().toString(36).substr(2, 5)}`
          }
        };

        const response = await axios.post(
          `${apiUrl}/albaran-activos`,
          albaranPayload,
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        this.$notify({
          type: "success",
          message: `Albarán ${numero} creado correctamente`
        });

        // Redirigir al detalle del albarán creado
        this.$router.push({
          name: "View Albaran",
          params: { id: response.data.data.id }
        });

      } catch (error) {
        console.error("Error creando albarán:", error);
        this.$notify({
          type: "danger",
          message: "Error al crear el albarán: " + (error.response?.data?.error?.message || "Error desconocido")
        });
      } finally {
        this.creating = false;
      }
    }
  }
};
</script>

<style scoped>
.form-group {
  margin-bottom: 1.5rem;
}

.form-control-label {
  font-weight: 600;
  color: #525f7f;
  margin-bottom: 0.5rem;
}
</style>
